import {
  CourseJSON,
  CourseModel,
  CourseModuleJSON,
  CourseModuleModel,
  CourseProgressSummaryJSON,
  CourseProgressSummaryModel,
  CourseResourceJSON,
} from './course.model'

export interface LearnerCourseEnrollmentProgressJSON {
  percentComplete?: number | string | null
  modulesTotal?: number | null
  modulesCompleted?: number | null
}

const parseNumeric = (value: number | string | null | undefined): number | undefined => {
  if (value === null || value === undefined) return undefined
  const num = typeof value === 'string' ? Number.parseFloat(value) : value
  return Number.isFinite(num) ? Number(num) : undefined
}

export interface LearnerCourseEnrollmentJSON {
  enrollmentId: string
  courseId: string
  courseTitle: string
  courseCode?: string | null
  enrollmentStatus?: string | null
  enrolledAt?: string | null
  progress?: LearnerCourseEnrollmentProgressJSON | null
  curriculumYear?: number | null
  curriculumSemester?: number | null
}

export interface LearnerProgrammeEnrollmentJSON {
  programmeId: string
  programmeTitle: string
  programmeDescription?: string | null
  durationMonths?: number | null
  isActive?: boolean | null
  curriculumYear?: number | null
  curriculumSemester?: number | null
  enrollments?: LearnerCourseEnrollmentJSON[] | null
}

export interface LearnerCourseProgressJSON {
  course: CourseJSON
  progress?: CourseProgressSummaryJSON | null
  modules?: CourseModuleJSON[] | null
}

export class LearnerCourseEnrollmentProgressModel {
  readonly percentComplete: number
  readonly modulesTotal?: number
  readonly modulesCompleted?: number

  constructor(options: { percentComplete?: number; modulesTotal?: number; modulesCompleted?: number }) {
    const percent =
      typeof options.percentComplete === 'string'
        ? Number.parseFloat(options.percentComplete)
        : options.percentComplete
    this.percentComplete = Number.isFinite(percent) ? Math.min(100, Math.max(0, Math.round(percent ?? 0))) : 0
    this.modulesTotal = options.modulesTotal
    this.modulesCompleted = options.modulesCompleted
  }

  static fromJSON(json?: LearnerCourseEnrollmentProgressJSON | null): LearnerCourseEnrollmentProgressModel | undefined {
    if (!json) return undefined
    return new LearnerCourseEnrollmentProgressModel({
      percentComplete: typeof json.percentComplete === 'string' ? Number.parseFloat(json.percentComplete) : json.percentComplete ?? undefined,
      modulesTotal: json.modulesTotal ?? undefined,
      modulesCompleted: json.modulesCompleted ?? undefined,
    })
  }

  get isCompleted(): boolean {
    return this.percentComplete >= 100
  }
}

export class LearnerCourseEnrollmentModel {
  readonly enrollmentId: string
  readonly courseId: string
  readonly courseTitle: string
  readonly courseCode?: string
  readonly enrollmentStatus?: string
  readonly enrolledAt?: string
  readonly progress?: LearnerCourseEnrollmentProgressModel
  readonly programmeId: string
  readonly programmeTitle: string
  readonly curriculumYear?: number
  readonly curriculumSemester?: number

  constructor(options: {
    enrollmentId: string
    courseId: string
    courseTitle: string
    courseCode?: string
    enrollmentStatus?: string
    enrolledAt?: string
    progress?: LearnerCourseEnrollmentProgressModel
    programmeId: string
    programmeTitle: string
    curriculumYear?: number
    curriculumSemester?: number
  }) {
    this.enrollmentId = options.enrollmentId
    this.courseId = options.courseId
    this.courseTitle = options.courseTitle
    this.courseCode = options.courseCode
    this.enrollmentStatus = options.enrollmentStatus
    this.enrolledAt = options.enrolledAt
    this.progress = options.progress
    this.programmeId = options.programmeId
    this.programmeTitle = options.programmeTitle
    this.curriculumYear = options.curriculumYear
    this.curriculumSemester = options.curriculumSemester
  }

  static fromJSON(json: LearnerCourseEnrollmentJSON, programme: LearnerProgrammeEnrollmentJSON): LearnerCourseEnrollmentModel {
    return new LearnerCourseEnrollmentModel({
      enrollmentId: json.enrollmentId,
      courseId: json.courseId,
      courseTitle: json.courseTitle,
      courseCode: json.courseCode ?? undefined,
      enrollmentStatus: json.enrollmentStatus ?? undefined,
      enrolledAt: json.enrolledAt ?? undefined,
      progress: LearnerCourseEnrollmentProgressModel.fromJSON(json.progress),
      programmeId: programme.programmeId,
      programmeTitle: programme.programmeTitle,
      curriculumYear: parseNumeric(json.curriculumYear ?? programme.curriculumYear ?? undefined),
      curriculumSemester: parseNumeric(json.curriculumSemester ?? programme.curriculumSemester ?? undefined),
    })
  }

  get progressPercent(): number {
    return this.progress?.percentComplete ?? 0
  }

  get modulesTotal(): number | undefined {
    return this.progress?.modulesTotal
  }

  get modulesCompleted(): number | undefined {
    return this.progress?.modulesCompleted
  }

  get isCompleted(): boolean {
    if (this.enrollmentStatus?.toLowerCase() === 'completed') return true
    return this.progress?.isCompleted ?? false
  }
}

export class LearnerProgrammeEnrollmentModel {
  readonly programmeId: string
  readonly programmeTitle: string
  readonly programmeDescription?: string
  readonly durationMonths?: number
  readonly isActive: boolean
  readonly enrollments: LearnerCourseEnrollmentModel[]
  readonly curriculumYear?: number
  readonly curriculumSemester?: number

  constructor(options: {
    programmeId: string
    programmeTitle: string
    programmeDescription?: string
    durationMonths?: number
    isActive?: boolean
    enrollments?: LearnerCourseEnrollmentModel[]
    curriculumYear?: number
    curriculumSemester?: number
  }) {
    this.programmeId = options.programmeId
    this.programmeTitle = options.programmeTitle
    this.programmeDescription = options.programmeDescription
    this.durationMonths = options.durationMonths
    this.isActive = options.isActive ?? true
    this.enrollments = options.enrollments ?? []
    this.curriculumYear = options.curriculumYear
    this.curriculumSemester = options.curriculumSemester
  }

  static fromJSON(json: LearnerProgrammeEnrollmentJSON): LearnerProgrammeEnrollmentModel {
    const enrollments = Array.isArray(json.enrollments)
      ? json.enrollments
          .filter((enrollment): enrollment is LearnerCourseEnrollmentJSON => Boolean(enrollment))
          .map((enrollment) => LearnerCourseEnrollmentModel.fromJSON(enrollment, json))
      : []

    return new LearnerProgrammeEnrollmentModel({
      programmeId: json.programmeId,
      programmeTitle: json.programmeTitle,
      programmeDescription: json.programmeDescription ?? undefined,
      durationMonths: json.durationMonths ?? undefined,
      isActive: json.isActive ?? true,
      enrollments,
      curriculumYear: parseNumeric(json.curriculumYear ?? undefined),
      curriculumSemester: parseNumeric(json.curriculumSemester ?? undefined),
    })
  }
}

export class LearnerCourseProgressModel {
  readonly course: CourseModel
  readonly progress?: CourseProgressSummaryModel
  readonly modules: CourseModuleModel[]

  constructor(options: { course: CourseModel; progress?: CourseProgressSummaryModel; modules?: CourseModuleModel[] }) {
    this.course = options.course
    this.progress = options.progress
    this.modules = options.modules ?? []
  }

  static fromJSON(json: LearnerCourseProgressJSON): LearnerCourseProgressModel {
    const course = CourseModel.fromJSON(json.course)

    const modules = Array.isArray(json.modules)
      ? json.modules
          .filter((module): module is CourseModuleJSON => Boolean(module))
          .map((module) => {
            const scopedResources: CourseResourceJSON[] = Array.isArray(module.resources)
              ? module.resources
              : json.course.resources ?? []
            return CourseModuleModel.fromJSON(module, scopedResources)
          })
      : []

    return new LearnerCourseProgressModel({
      course,
      progress: CourseProgressSummaryModel.fromJSON(json.progress),
      modules,
    })
  }

  get percentComplete(): number {
    return this.progress?.percentComplete ?? 0
  }

  get modulesCompleted(): number | undefined {
    return this.progress?.modulesCompleted
  }

  get modulesTotal(): number | undefined {
    return this.progress?.modulesTotal
  }
}

export type LearnerProgrammeEnrollmentResponseJSON = LearnerProgrammeEnrollmentJSON[]
